import type { TimeSlots } from '@/types/TimeSlot';

import type { UserStatus, GoogleSession } from './company';

interface NodeData {
  prompt: string;
}

export interface FollowUp {
  uuid: string;
  name: string;
  triggerDays: number;
  templateName: string;
  userStatuses: UserStatus[];
}

interface Batch {
  name: string;
  status: string; // TODO Hardcoded for now
  origin: string;
  aiAgentId: string;
  createdAt: string;
  numRecipients: number;
  templateName: string | null;
}

export interface QualificationVariable {
  name: string;
  type: string;
  uuid?: string;
  description: string;
}

export interface AIFunction {
  uuid: string;
  name: string;
  category: string;
  extraData: object;
  description: string;
  fileMetadata: OpenAIFileMetadata | null;
  parameters: {
    properties: {
      [key: string]: {
        description: string;
        type: string;
      };
    };
    required: string[];
    type: string;
  };
}

export interface PdfFunction {
  uuid: string;
  name: string;
  file: File | null;
  description: string;
  fileMetadata: OpenAIFileMetadata | null;
}

export interface OpenAIFileMetadata {
  id: string;
  bytes: number;
  purpose: string;
  filename: string;
  createdAt: string;
}

export interface FileUploadResponse {
  id: string;
  bytes: number;
  filename: string;
  vectorStoreId: string;
}

export interface FilesListResponse {
  files: OpenAIFileMetadata[];
  vectorStoreId: string | null;
}

export interface CalendarMeeting {
  uuid: string;
  name: string;
  duration: number;
  crm_owner: boolean;
  description: string;
  invite_user: boolean;
  working_hours: TimeSlots;
  google_sessions: GoogleSession[]; // I cannot use Camel here because this is defined in extraData
  routing: 'round_robin' | 'first_available';
}

export interface AiAgentEdge {
  uuid: string;
  source: string;
  target: string;
  data: { function: AIFunction };
}

export interface AiAgentNode {
  uuid: string;
  label: string;
  data: NodeData;
  isRootNode: boolean;
  parent: string | null;
}

export interface Evaluation {
  uuid: string;
}

interface AiAgent {
  name: string;
  uuid: string;
  model: string;
  prompt: string;
  phoneNumber: string;
  nodes: AiAgentNode[];
  edges: AiAgentEdge[];
  firstMessage: string;
  functions: AIFunction[];
  personalityPrompt: string;
  evaluations: Evaluation[];
  phoneNumberId: string | null;
  verifiedPhoneName: string | null;
  displayPhoneNumber: string | null;
  phoneQualityRating: 'GREEN' | 'YELLOW' | 'RED' | null;
}

export interface WhatsAppPhoneNumber {
  id: string;
  verified_name: string;
  display_phone_number: string;
  quality_rating: 'GREEN' | 'YELLOW' | 'RED';
}

export interface Test {
  uuid: string;
  guide: string;
  question: string;
  result?: {
    answer: string;
    explanation: string;
    score: number;
    show: boolean;
  };
}

export interface CrmConnection {
  uuid: string;
  company_id: string;
  created_at: string;
  updated_at: string;
  provider: 'odoo' | 'hubspot';
  config: OdooConfig | HubspotConfig;
  statusMappings: Record<string, string>;
}

interface OdooConfig {
  db: string;
  url: string;
  secret: string;
  username: string;
}

interface HubspotConfig {
  api_key: string;
}

const defaultAgent = {
  name: '',
  uuid: '',
  nodes: [],
  edges: [],
  model: '',
  prompt: '',
  functions: [],
  phoneNumber: '',
  evaluations: [],
  firstMessage: '',
  templateName: '',
  phoneNumberId: null,
  verifiedPhoneName: null,
  displayPhoneNumber: null,
  phoneQualityRating: null,
  followupTemplateName: '',
  personalityPrompt: '## Identity and Goal\n\n\n## Style guidelines\n\n\n## Response guidelines\n\n\n## Knowledge',
};

export const useAiAgents = defineStore('ai-agents', () => {
  const { $sellebrate } = useNuxtApp();
  const companyStore = useCompanyStore();
  const PERSISTED_AGENT_KEY = 'selectedAgentId';

  const fetchAgents = () => $sellebrate<Record<string, AiAgent>>('/company/ai_agents');
  const batches = ref<Batch[]>([]);
  const crmConnections = ref<CrmConnection[]>([]);

  const { state: aiAgents } = useAsyncState(fetchAgents(), {});
  const selectedAgent = ref<AiAgent>(defaultAgent);

  const followUps = ref<Array<FollowUp>>([]);
  const qualificationVariables = ref<QualificationVariable[]>([]);

  const addFollowup = async (followup: FollowUp) => {
    await $sellebrate(`/company/follow_ups?ai_agent_id=${selectedAgent.value.uuid}`, {
      method: 'POST',
      body: followup,
    });
  };

  const deleteFollowup = async (uuid: string) => {
    await $sellebrate(`/company/follow_ups/${uuid}`, {
      method: 'DELETE',
    });
  };

  const fetchFollowups = async () => {
    const response = await $sellebrate<Array<FollowUp>>(`/company/follow_ups?ai_agent_id=${selectedAgent.value.uuid}`);
    followUps.value = response;
  };

  const updateFollowup = async (followup: FollowUp) => {
    await $sellebrate(`/company/follow_ups/${followup.uuid}`, {
      method: 'PUT',
      body: followup,
    });
  };

  const selectAgent = (agentId: string) => {
    selectedAgent.value = aiAgents.value[agentId] ?? defaultAgent;

    // Persist selection on the client so refresh keeps the chosen agent
    if (import.meta.client) {
      if (selectedAgent.value.uuid) {
        localStorage.setItem(PERSISTED_AGENT_KEY, selectedAgent.value.uuid);
      }
      else {
        localStorage.removeItem(PERSISTED_AGENT_KEY);
      }
    }

    if (selectedAgent.value.uuid) {
      fetchFollowups();
    }
  };
  const refetchAiAgents = async (agentUuidToSelect?: string) => {
    const agents = await fetchAgents();
    aiAgents.value = agents;
    selectAgent(agentUuidToSelect ?? selectedAgent.value.uuid);
    return agents;
  };

  watch([companyStore, aiAgents], ([store, _]) => {
    // Wait until agents are loaded to decide selection
    if (!Object.keys(aiAgents.value ?? {}).length) return;

    // Try restoring from localStorage first
    const persistedId = import.meta.client ? localStorage.getItem(PERSISTED_AGENT_KEY) : null;
    if (persistedId && aiAgents.value[persistedId]) {
      if (selectedAgent.value.uuid !== persistedId) selectAgent(persistedId);
      return;
    }

    // Fallback to company default agent if nothing selected yet
    if (!selectedAgent.value.uuid && store.company?.prodAiAgentId && aiAgents.value[store.company.prodAiAgentId]) {
      selectAgent(store.company.prodAiAgentId);
    }
  }, { immediate: true });

  const { state: tests } = useAsyncState($sellebrate<Array<Test>>('/company/tests'), [], { shallow: false });

  const createAgent = async () => {
    const response = await $sellebrate<AiAgent>(`/company/ai_agent/${selectedAgent.value.uuid}`, {
      method: 'POST',
    });

    if (response.uuid) {
      await refetchAiAgents(response.uuid);
    }
  };

  const removeAgent = async () => {
    await $sellebrate(`/company/ai_agent/${selectedAgent.value.uuid}`, {
      method: 'DELETE',
    });
    await refetchAiAgents();
  };

  const deployAgent = async () => {
    await $sellebrate(`/company/ai_agent/deploy/${selectedAgent.value.uuid}`, {
      method: 'PUT',
    });
  };

  const savePrompt = async (prompt: string) => {
    await $sellebrate(`/company/prompt/${selectedAgent.value.uuid}`, {
      method: 'put',
      body: { prompt },
    });
    await refetchAiAgents();
  };

  const saveName = async (name: string) => {
    await $sellebrate(`/company/name/${selectedAgent.value.uuid}?name=${name}`, {
      method: 'put',
    });
    await refetchAiAgents();
  };

  const createBatch = async (
    batchName: string,
    usersFile: File,
    templateName: string,
    sendToExistingUsers: boolean,
  ) => {
    const formData = new FormData();
    formData.append('users', usersFile);

    await $sellebrate(
      `/chats/start_chats?batch_name=${batchName}&ai_agent_id=${selectedAgent.value.uuid}&`
      + `template_name=${templateName}&send_to_existing_users=${sendToExistingUsers}`,
      {
        method: 'POST',
        body: formData,
      },
    );
  };

  const fetchBatches = async () => {
    const response = await $sellebrate<Array<Batch>>('/chats/batch_conversations');
    batches.value = response.map(batch => ({
      ...batch,
      status: 'sent',
      promptVersion: 'v3',
    }));
  };

  const saveOrchestratorFlow = async (
    prompt: string,
    firstMessage: string,
    personalityPrompt: string,
    nodes: AiAgentNode[],
    edges: AiAgentEdge[],
  ) => {
    await $sellebrate('/company/ai_agent', {
      method: 'put',
      body: {
        ...selectedAgent.value,
        edges,
        nodes,
        prompt,
        firstMessage,
        personalityPrompt,
      },
    });
    await refetchAiAgents();
  };

  fetchBatches();

  const addTest = async (test: Test) => {
    return await $sellebrate<{ uuid: string }>('/company/tests', {
      method: 'POST',
      body: {
        guide: test.guide,
        question: test.question,
      },
    });
  };

  const runTests = async () => {
    return await $sellebrate<Array<Test['result'] & { uuid: string }>>(
      '/company/run_tests',
      {
        method: 'POST',
      },
    );
  };

  const deleteTest = async (test: Test) => {
    return await $sellebrate(`/company/tests/${test.uuid}`, {
      method: 'DELETE',
    });
  };

  const editTest = async (test: Test) => {
    return await $sellebrate(`/company/tests/${test.uuid}`, {
      method: 'PUT',
      body: {
        guide: test.guide,
        question: test.question,
      },
    });
  };

  const createPdfFunction = async (aiAgent: AiAgent, pdfFunction: PdfFunction) => {
    if (!pdfFunction.file) return;
    const formData = new FormData();
    formData.append('name', pdfFunction.name);
    formData.append('description', pdfFunction.description);
    formData.append('pdf_file', pdfFunction.file);

    const response = await $sellebrate<{ uuid: string; fileMetadata: OpenAIFileMetadata }>
      (`/company/ai_agent/${aiAgent.uuid}/pdf_function`, {
        method: 'POST',
        body: formData,
      });

    aiAgent.functions.push({
      extraData: {},
      uuid: response.uuid,
      category: 'send_pdf',
      name: pdfFunction.name,
      fileMetadata: response.fileMetadata,
      description: pdfFunction.description,
      parameters: {
        required: [],
        properties: {},
        type: 'object',
      },
    });
  };

  const editPdfFunction = async (aiAgent: AiAgent, pdfFunction: PdfFunction) => {
    const formData = new FormData();
    formData.append('name', pdfFunction.name);
    formData.append('description', pdfFunction.description);
    if (pdfFunction.file) formData.append('pdf_file', pdfFunction.file);

    const fileMetadata = await $sellebrate<OpenAIFileMetadata>
      (`/company/ai_agent/${aiAgent.uuid}/pdf_function/${pdfFunction.uuid}`, {
        method: 'PUT',
        body: formData,
      });
    const functionIndex = aiAgent.functions.findIndex(f => f.uuid === pdfFunction.uuid);
    aiAgent.functions[functionIndex]!.name = pdfFunction.name;
    aiAgent.functions[functionIndex]!.description = pdfFunction.description;
    aiAgent.functions[functionIndex]!.fileMetadata = fileMetadata;
  };

  const deletePdfFunction = async (aiAgent: AiAgent, functionId: string) => {
    await $sellebrate(`/company/ai_agent/${aiAgent.uuid}/pdf_function/${functionId}`, {
      method: 'DELETE',
    });
    aiAgent.functions = aiAgent.functions.filter(f => f.uuid !== functionId);
  };

  const createMeetingFunction = async (meeting: CalendarMeeting) => {
    const response = await $sellebrate<AIFunction>
      (`/company/ai_agent/${selectedAgent.value.uuid}/calendar_meeting`, { body: meeting, method: 'POST' });

    selectedAgent.value.functions.push(response);
  };

  const editMeetingFunction = async (meeting: CalendarMeeting) => {
    const response = await $sellebrate<AIFunction>
      (`/company/ai_agent/${selectedAgent.value.uuid}/calendar_meeting`, { method: 'PUT', body: meeting });

    // Find the function by the meeting UUID in extraData, not the function UUID
    // This handles cases where the function UUID changes (e.g., when changing meeting type)
    const functionIndex = selectedAgent.value.functions.findIndex((f) => {
      if ('calendar_meeting' in f.extraData) {
        const cm = f.extraData.calendar_meeting as CalendarMeeting;
        return cm.uuid === meeting.uuid;
      }
      return false;
    });

    if (functionIndex !== -1) {
      // Use splice to properly trigger Vue's reactivity system
      selectedAgent.value.functions.splice(functionIndex, 1, { ...response });
    }
  };

  const deleteMeetingFunction = async (aiFunction: AIFunction) => {
    if (!('calendar_meeting' in aiFunction.extraData)) return;
    const calendarMeeting = aiFunction.extraData.calendar_meeting as CalendarMeeting;
    await $sellebrate(`/company/ai_agent/${selectedAgent.value.uuid}/calendar_meeting/${calendarMeeting.uuid}`, {
      method: 'DELETE',
    });
    selectedAgent.value.functions = selectedAgent.value.functions.filter(f => f.uuid !== aiFunction.uuid);
  };

  const fetchCrmConnections = async () => {
    const response = await $sellebrate<CrmConnection[]>('/company/crm_connections');
    crmConnections.value = response;
  };

  const createCrmConnection = async (provider: CrmConnection['provider'], config: CrmConnection['config']) => {
    const response = await $sellebrate<CrmConnection>('/company/crm_connections', {
      method: 'POST',
      body: { config, provider },
    });
    crmConnections.value.push(response);
    return response;
  };

  const getCrmConnection = async (connectionId: string) => {
    return await $sellebrate<CrmConnection>(`/company/crm_connections/${connectionId}`);
  };

  const updateCrmConnection = async (
    connectionId: string,
    provider: CrmConnection['provider'],
    config: CrmConnection['config'],
    statusMappings?: Record<string, string>,
  ) => {
    const response = await $sellebrate<CrmConnection>(
      `/company/crm_connections/${connectionId}`,
      {
        method: 'PUT',
        body: { config, provider, status_mappings: statusMappings ?? {} },
      },
    );
    const index = crmConnections.value.findIndex(conn => conn.uuid === connectionId);
    if (index !== -1) {
      crmConnections.value[index] = response;
    }
    return response;
  };

  const deleteCrmConnection = async (connectionId: string) => {
    await $sellebrate(`/company/crm_connections/${connectionId}`, {
      method: 'DELETE',
    });
    crmConnections.value = crmConnections.value.filter(conn => conn.uuid !== connectionId);
  };

  const testCrmConnection = async (
    provider: CrmConnection['provider'],
    config: CrmConnection['config'],
  ) => {
    const response = await $sellebrate<{ success: boolean }>('/company/crm_connections/test', {
      method: 'POST',
      body: { config, provider },
    });
    return response.success;
  };

  const callAIAgent = async (chatId: string) => {
    await $sellebrate(`/chats/retell/${chatId}/phone_call`, {
      method: 'POST',
    });
  };

  const fetchQualificationVariables = async () => {
    if (!selectedAgent.value.uuid) return;

    try {
      const response = await $sellebrate<{ qualificationVariables: QualificationVariable[] }>(
        `/company/ai_agent/${selectedAgent.value.uuid}/qualification_variables`,
      );
      qualificationVariables.value = response.qualificationVariables || [];
      return qualificationVariables.value;
    }
    catch (error) {
      console.error('Failed to fetch qualification variables:', error);
      qualificationVariables.value = [];
      return [];
    }
  };

  const saveQualificationVariables = async (variables: QualificationVariable[]) => {
    if (!selectedAgent.value.uuid) return;

    try {
      await $sellebrate(`/company/ai_agent/${selectedAgent.value.uuid}/qualification_variables`, {
        method: 'PUT',
        body: { qualification_variables: variables },
      });
      qualificationVariables.value = [...variables];
    }
    catch (error) {
      console.error('Failed to save qualification variables:', error);
      throw error;
    }
  };

  const uploadFileToAgent = async (file: File) => {
    if (!selectedAgent.value.uuid) return;

    const formData = new FormData();
    formData.append('file', file);

    return await $sellebrate<FileUploadResponse>(
      `/company/ai_agent/${selectedAgent.value.uuid}/files`,
      {
        method: 'POST',
        body: formData,
      },
    );
  };

  const fetchAgentFiles = async () => {
    if (!selectedAgent.value.uuid) return { files: [], vector_store_id: null };

    return await $sellebrate<FilesListResponse>(
      `/company/ai_agent/${selectedAgent.value.uuid}/files`,
    );
  };

  const deleteAgentFile = async (fileId: string) => {
    if (!selectedAgent.value.uuid) return;

    await $sellebrate(
      `/company/ai_agent/${selectedAgent.value.uuid}/files/${fileId}`,
      {
        method: 'DELETE',
      },
    );
  };

  const uploadUrlToAgent = async (url: string) => {
    if (!selectedAgent.value.uuid) return;

    return await $sellebrate<FileUploadResponse>(
      `/company/ai_agent/${selectedAgent.value.uuid}/upload_url`,
      {
        method: 'POST',
        query: { url },
      },
    );
  };

  const fetchAgentUrls = async () => {
    if (!selectedAgent.value.uuid) return { files: [], vector_store_id: null };

    return await $sellebrate<FilesListResponse>(
      `/company/ai_agent/${selectedAgent.value.uuid}/urls`,
    );
  };

  const deleteAgentUrl = async (fileId: string) => {
    if (!selectedAgent.value.uuid) return;

    await $sellebrate(
      `/company/ai_agent/${selectedAgent.value.uuid}/urls/${fileId}`,
      {
        method: 'DELETE',
      },
    );
  };

  const fetchWhatsAppPhoneNumbers = async () => {
    try {
      const response = await $sellebrate<WhatsAppPhoneNumber[]>('/company/facebook/phone-numbers');
      return response || [];
    }
    catch (error) {
      console.error('Failed to fetch WhatsApp phone numbers:', error);
      return [];
    }
  };

  const updateAgentPhoneNumber = async (phone: WhatsAppPhoneNumber) => {
    if (!selectedAgent.value.uuid) return;

    await $sellebrate(`/company/ai_agent/${selectedAgent.value.uuid}/phone-number`, {
      method: 'PATCH',
      query: { phone_number_id: phone.id },
    });

    // Update local state
    selectedAgent.value.phoneNumberId = phone.id;
    selectedAgent.value.displayPhoneNumber = phone.display_phone_number;
    selectedAgent.value.verifiedPhoneName = phone.verified_name;
  };

  return {
    tests,
    addTest,
    batches,
    runTests,
    editTest,
    saveName,
    aiAgents,
    followUps,
    savePrompt,
    deleteTest,
    createAgent,
    removeAgent,
    deployAgent,
    selectAgent,
    createBatch,
    addFollowup,
    callAIAgent,
    fetchBatches,
    updateFollowup,
    fetchFollowups,
    deleteFollowup,
    crmConnections,
    fetchAgentUrls,
    deleteAgentUrl,
    editPdfFunction,
    refetchAiAgents,
    fetchAgentFiles,
    deleteAgentFile,
    uploadUrlToAgent,
    getCrmConnection,
    deletePdfFunction,
    createPdfFunction,
    testCrmConnection,
    uploadFileToAgent,
    editMeetingFunction,
    fetchCrmConnections,
    createCrmConnection,
    updateCrmConnection,
    deleteCrmConnection,
    saveOrchestratorFlow,
    deleteMeetingFunction,
    createMeetingFunction,
    qualificationVariables,
    updateAgentPhoneNumber,
    fetchWhatsAppPhoneNumbers,
    saveQualificationVariables,
    fetchQualificationVariables,
    selectedAgent: toReactive(selectedAgent),
  };
});
